import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import { useQuery } from '@tanstack/react-query'
import SydLabel from '../../../commonDesign/SydLabel'
import SydInput from '../../../commonDesign/SydInput'
import SydSelect from '../../../commonDesign/SydSelect'
import { postNamedQuery } from '../../../../service'

function useOptionsFrom (optionsFrom) {
  return useQuery({
    queryKey: ['scheduler-options', optionsFrom?.domain, optionsFrom?.query],
    queryFn: async () => {
      const { data } = await postNamedQuery(optionsFrom.domain, optionsFrom.query, {})
      return data
    },
    enabled: !!optionsFrom?.domain && !!optionsFrom?.query
  })
}

function SelectField ({ field, value, onChange }) {
  const { data: options, isLoading } = useOptionsFrom(field.optionsFrom)

  const selectOptions = React.useMemo(() => {
    if (!options || !Array.isArray(options)) return []
    return options.map(opt => ({
      value: typeof opt === 'string' ? opt : getNestedValue(opt, field.optionsFrom.valuePath),
      label: typeof opt === 'string' ? opt : getNestedValue(opt, field.optionsFrom.labelPath)
    }))
  }, [options, field.optionsFrom])

  return (
    <SydLabel label={field.label}>
      <SydSelect
        options={[{ label: isLoading ? 'Loading...' : 'Select...', value: '' }, ...selectOptions]}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </SydLabel>
  )
}

function TextField ({ field, value, onChange }) {
  return (
    <SydLabel label={field.label}>
      <SydInput
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || ''}
      />
    </SydLabel>
  )
}

function JsonField ({ field, value, onChange }) {
  const [text, setText] = useState(value ? JSON.stringify(value, null, 2) : field.placeholder || '{}')

  useEffect(() => {
    if (value) {
      setText(JSON.stringify(value, null, 2))
    }
  }, [value])

  const handleBlur = useCallback(() => {
    try {
      const parsed = JSON.parse(text)
      onChange(parsed)
    } catch {
      // leave as-is until valid
    }
  }, [text, onChange])

  return (
    <SydLabel label={field.label}>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleBlur}
        placeholder={field.placeholder || '{}'}
        style={{
          width: '100%',
          minHeight: 80,
          fontFamily: 'monospace',
          fontSize: '0.85rem',
          border: '1px solid #ccc',
          borderRadius: 4,
          padding: '0.5rem',
          resize: 'vertical'
        }}
      />
    </SydLabel>
  )
}

function DynamicParameterFields ({ target, input, onChange }) {
  const fields = target?.parameterMappings?.fields || []

  const handleFieldChange = useCallback((path, value) => {
    onChange(setNestedValue(input, path, value))
  }, [input, onChange])

  if (!fields.length) {
    return (
      <div style={{ fontSize: '0.85rem', color: '#888' }}>
        No parameters needed for this target.
      </div>
    )
  }

  return (
    <Grid container spacing={1}>
      {fields.map((field) => (
        <Grid item xs={12} key={field.path}>
          {field.editor === 'select' && field.optionsFrom ? (
            <SelectField
              field={field}
              value={getNestedValue(input, field.path)}
              onChange={(v) => handleFieldChange(field.path, v)}
            />
          ) : field.editor === 'json' ? (
            <JsonField
              field={field}
              value={getNestedValue(input, field.path)}
              onChange={(v) => handleFieldChange(field.path, v)}
            />
          ) : (
            <TextField
              field={field}
              value={getNestedValue(input, field.path)}
              onChange={(v) => handleFieldChange(field.path, v)}
            />
          )}
        </Grid>
      ))}
    </Grid>
  )
}

function getNestedValue (obj, path) {
  if (!path) return obj
  return path.split('.').reduce((acc, key) => acc?.[key], obj)
}

function setNestedValue (obj, path, value) {
  if (!path) return value
  const keys = path.split('.')
  if (keys.length === 1) return { ...obj, [path]: value }
  const [head, ...rest] = keys
  return { ...obj, [head]: setNestedValue(obj?.[head] ?? {}, rest.join('.'), value) }
}

const fieldShape = PropTypes.shape({
  path: PropTypes.string.isRequired,
  label: PropTypes.string,
  editor: PropTypes.string,
  placeholder: PropTypes.string,
  optionsFrom: PropTypes.shape({
    domain: PropTypes.string,
    query: PropTypes.string,
    valuePath: PropTypes.string,
    labelPath: PropTypes.string
  })
})

SelectField.propTypes = {
  field: fieldShape.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired
}

TextField.propTypes = {
  field: fieldShape.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired
}

JsonField.propTypes = {
  field: fieldShape.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired
}

DynamicParameterFields.propTypes = {
  target: PropTypes.shape({
    parameterMappings: PropTypes.shape({
      fields: PropTypes.arrayOf(fieldShape)
    })
  }),
  input: PropTypes.object,
  onChange: PropTypes.func.isRequired
}

export default DynamicParameterFields
